import { CreateDepartmentResponse } from 'compliance-api';
import { ReactNode, useCallback, useId, useState } from 'react';
import { CreateDepartmentModal } from '@/modules/admin-dashboard/components/modals/CreateDepartmentModal.tsx';
import { useCurrentUserDepartmentsQuery } from '@/modules/admin-dashboard/queries/department.ts';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { PlusBlueIcon } from 'ui/icons';

type baseProps = {
  label: string;
  placeholder?: string;
  formFieldTitle: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  enableCreation?: boolean;
};

type singleProps = {
  isMulti: false;
  value?: SelectPaginateOption<number>;
  handleSelectDepartments: (options: SelectPaginateOption<number>, title: string) => void;
  defaultValue?: SelectPaginateOption<number>;
};

type multiProps = {
  isMulti: true;
  value?: SelectPaginateOption<number>[];
  handleSelectDepartments: (options: SelectPaginateOption<number>[], title: string) => void;
  defaultValue?: SelectPaginateOption<number>[];
};

type DepartmentSelectPaginatedProps = baseProps & (singleProps | multiProps);

const DepartmentsSelectPaginated = ({
  label,
  error,
  value,
  isMulti,
  className,
  placeholder,
  defaultValue,
  formFieldTitle,
  enableCreation,
  handleSelectDepartments,
  disabled = false,
}: DepartmentSelectPaginatedProps) => {
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());

  const { fetchNextPage } = useCurrentUserDepartmentsQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_name: '',
    },
    { disabled: true, cacheKey: useId() },
  );

  const handleFetchNextDepartments = async (
    page: number,
    search?: string,
  ): Promise<SelectPaginateOption<number>[]> => {
    const departmentsData = await fetchNextPage({ pageParam: { page, search } });

    const departments = departmentsData?.data?.pages?.at(-1)?.data || [];

    return departments.map(({ id, name }) => ({ label: name as ReactNode, value: id }));
  };

  const handleChange = useCallback(
    (options: SelectPaginateOption<number> | SelectPaginateOption<number>[]) => {
      if (!Array.isArray(options) && !options.value) return;
      if (Array.isArray(options) && options.some(({ value }) => !value)) return;

      handleSelectDepartments(options as any, formFieldTitle);
    },
    [formFieldTitle, handleSelectDepartments],
  );

  const handleDepartmentAdded = useCallback(
    (response: CreateDepartmentResponse) => {
      setCacheUniq(Math.random());

      const option = { label: response.department.name, value: response.department.id };
      if (isMulti) {
        handleSelectDepartments([...(value || []), option], formFieldTitle);
      } else {
        handleSelectDepartments(option, formFieldTitle);
      }
    },
    [formFieldTitle, handleSelectDepartments, isMulti, value],
  );

  const addedLastOption = enableCreation
    ? {
        label: (
          <div className="flex gap-2">
            <img src={PlusBlueIcon} alt="add" />
            <span>Add department</span>
          </div>
        ),
        onClick: () => {
          setIsCreationOpen(true);
        },
      }
    : undefined;

  return (
    <div>
      <SelectPaginate
        autoHeight
        cacheUniqs={[cacheUniq]}
        placeholder={placeholder}
        isMulti={isMulti}
        disabled={disabled}
        defaultValue={defaultValue}
        error={error}
        value={enableCreation ? value || null : value}
        className={className}
        onChange={handleChange}
        label={label}
        onAsync={handleFetchNextDepartments}
        debounceTimeoutSearch={700}
        addedLastOption={addedLastOption}
      />
      {isCreationOpen && (
        <CreateDepartmentModal
          open={isCreationOpen}
          onSuccess={handleDepartmentAdded}
          onClose={() => setIsCreationOpen(false)}
        />
      )}
    </div>
  );
};

export default DepartmentsSelectPaginated;
